/**
 * Session Expiry Detection Hook
 *
 * Detects when a user's session expires unexpectedly (not from intentional logout)
 * and triggers the session expired modal.
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { useTurnkeyWallet } from '@/providers/TurnkeyProvider';

export function useSessionExpiry() {
  const { isAuthenticated, isLoading } = useTurnkeyWallet();
  const [showExpiredModal, setShowExpiredModal] = useState(false);

  // Track whether user was previously authenticated
  const wasAuthenticatedRef = useRef<boolean | null>(null);

  // Flag to mark intentional logout (set before calling logout)
  const isIntentionalLogoutRef = useRef(false);

  // Track if initial load is complete
  const initialLoadCompleteRef = useRef(false);

  /**
   * Call this before signing out intentionally to prevent the modal from showing
   */
  const markIntentionalLogout = useCallback(() => {
    isIntentionalLogoutRef.current = true;
  }, []);

  useEffect(() => {
    // Wait for initial load to complete before tracking auth state
    if (isLoading) {
      return;
    }

    // Mark initial load as complete after first non-loading state
    if (!initialLoadCompleteRef.current) {
      initialLoadCompleteRef.current = true;
      wasAuthenticatedRef.current = isAuthenticated;
      return;
    }

    // Detect transition from authenticated to not authenticated
    if (wasAuthenticatedRef.current === true && isAuthenticated === false) {
      // Only show modal if this wasn't an intentional logout
      if (!isIntentionalLogoutRef.current) {
        setShowExpiredModal(true);
      }
      // Reset the flag after handling
      isIntentionalLogoutRef.current = false;
    }

    // If user signs in, close the modal
    if (isAuthenticated) {
      setShowExpiredModal(false);
    }

    // Update the previous state
    wasAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated, isLoading]);

  return {
    showExpiredModal,
    setShowExpiredModal,
    markIntentionalLogout,
  };
}
